import { useState, useEffect, useMemo } from 'react';
import { fetchCategories } from '../services/apiService';
import { filterNonEmptyCategories, pickCategoryName } from '../utils/categoryUtils';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * Hero "category bubbles".
 *
 * History / why this exists:
 *   The hero bubbles used to be hardcoded translated labels dropped straight
 *   into `?search=<label>` free-text search. That was broken two ways:
 *     1. Results changed with the UI language (the query string itself was the
 *        translated word — "Pain relief" vs "Schmerzmittel").
 *     2. Free-text search matched the wrong things (e.g. "Pain" matched the
 *        French word for bread → food in the results).
 *
 *   The fix is to drive the bubbles from the REAL category taxonomy (the same
 *   source the sidebar / top bar use) and navigate by the stable English
 *   category key. No hardcoded label→key mapping to drift out of sync, routes
 *   are always valid, and switching language only changes the visible label —
 *   never the products.
 *
 * Returns ready-to-render bubbles: `{ id, key, label, de, color, delay }`.
 * `key` is the English category id used for routing (see goToCategory);
 * `label` is localized for display; `de` feeds the results-page title.
 *
 * Fails safe: if categories can't be loaded it returns `[]`, so the hero
 * simply shows no bubbles rather than broken ones.
 *
 * @param {number} [max=7] — cap how many bubbles to show.
 */

// Decorative gradient palette, cycled by index so colors are decoupled from
// any specific category (categories are dynamic; colors are purely cosmetic).
const BUBBLE_COLORS = [
  'from-pink-400 to-rose-500',
  'from-yellow-400 to-red-500',
  'from-green-400 to-emerald-500',
  'from-blue-400 to-cyan-500',
  'from-red-400 to-pink-500',
  'from-purple-400 to-indigo-500',
  'from-teal-400 to-green-500',
];

export const useCategoryBubbles = (max = 7) => {
  const { language } = useLanguage();
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    let active = true;
    fetchCategories()
      .then((res) => {
        if (active) setCategories(Array.isArray(res?.data) ? res.data : []);
      })
      .catch(() => {
        if (active) setCategories([]);
      });
    return () => {
      active = false;
    };
  }, []);

  return useMemo(() => {
    return filterNonEmptyCategories(categories)
      .slice(0, max)
      .map((node, i) => {
        const c = node.category || {};
        const key = (c.en || '').trim();
        return {
          id: key || String(i),
          key,
          // Localized display name (labels[lang] → de → key). Always renders.
          label: pickCategoryName(c, language),
          de: c.de || c.en || '',
          color: BUBBLE_COLORS[i % BUBBLE_COLORS.length],
          delay: i * 0.1,
        };
      })
      .filter((b) => b.key && b.label);
  }, [categories, language, max]);
};

export default useCategoryBubbles;
